import { useEffect } from "react";

import { type GridReadyEvent, type RowClickedEvent } from "ag-grid-community";

import { Button, TableFiltersLine, useFiltersLine, Table } from "@synthesis/shared-ui";
import { IconClose } from "@synthesis/shared-icons";
import { parseAsString, useQueryStates } from "nuqs";
import { resolveMonitoringMetric } from "../../../shared/utils/monitoring-filters";
import {
  SYSTEM_PRIMARY_METRICS,
  SYSTEM_PAYMENT_METRICS,
  SYSTEM_FINANCE_METRICS,
  ACTIVE_DEADLINE_METRICS,
  ACTIVE_PAYMENT_METRICS,
  ACTIVE_INVOICE_METRICS,
  ACTIVE_FINANCE_METRICS,
} from "../../monitoring-page/monitoring-page.lib";

import { IconFileAddFill } from "@synthesis/shared-icons";
import { cn, useSearch } from "@synthesis/shared-utils";

import { useApplicationDictionariesContext } from "../../application-detail-page/application-dictionaries-context";
import { useNavigate } from "@tanstack/react-router";
import { useClientType } from "../../../shared/hooks/use-client-type";
import { observer } from "mobx-react-lite";
import { useUserRoles } from "../../../shared/hooks/use-user-roles";

import { useApplicationsTableColumns } from "../hooks/use-applications-table-columns";
import { useApplicationsTableDatasource } from "../hooks/use-applications-table-datasource";
import { DEFAULT_COL_DEF, TABLE_ROWS_PER_PAGE, usePageStore, type ApplicationsTableItem } from "../applications-page.lib";

type ApplicationsTableProps = {
  className?: string;
};

type ApplicationsEmptyOverlayProps = {
  /** Заявитель может создавать заявки — для него показывается заглушка «Создайте новую заявку» по макету. */
  hasCreateAction: boolean;
  overlayType: "noRows" | "noMatchingRows";
};

/** Пустой список (noRows) и «нет совпадений после фильтров» (noMatchingRows) — разные русские оверлеи. */
const ApplicationsEmptyOverlay = ({ hasCreateAction, overlayType }: ApplicationsEmptyOverlayProps) =>
  overlayType === "noRows" && hasCreateAction ? (
    <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-center">
      <IconFileAddFill className="text-neutrals-80 size-12" />
      <p className="subtitle-base text-type-common">Создайте новую заявку</p>
      <p className="caption-xs text-type-minor">У вас пока нет заявок, вы можете создать новую заявку сейчас</p>
    </div>
  ) : (
    <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-center">
      <p className="subtitle-sm text-type-common">Заявок нет</p>
      <p className="paragraph-sm text-type-minor">Попробуйте изменить поиск или фильтры</p>
    </div>
  );

export const ApplicationsTable = observer(({ className }: ApplicationsTableProps) => {
  const navigate = useNavigate();
  const clientType = useClientType();
  const [search] = useSearch();
  const [monitoringSearch, setMonitoringSearch] = useQueryStates({ monitoringMetric: parseAsString, railway: parseAsString });
  const monitoringMetric = [
    ...SYSTEM_PRIMARY_METRICS,
    ...SYSTEM_PAYMENT_METRICS,
    ...SYSTEM_FINANCE_METRICS,
    ...ACTIVE_DEADLINE_METRICS,
    ...ACTIVE_PAYMENT_METRICS,
    ...ACTIVE_INVOICE_METRICS,
    ...ACTIVE_FINANCE_METRICS,
  ].find((metric) => metric.id === resolveMonitoringMetric(monitoringSearch.monitoringMetric));
  const pageStore = usePageStore();
  const userRoles = useUserRoles();
  const { isLoading: isDictionariesLoading, dictionaries } = useApplicationDictionariesContext();
  const isInternalContourRole =
    clientType === "internal" &&
    (userRoles.isInternalNgSecretary || userRoles.isInternalApparatusNg || userRoles.isInternalNtpChief || userRoles.isInternalNtpResponsible);
  const isOperator = (clientType === "external" && userRoles.isExternalOperator) || (clientType === "internal" && userRoles.isInternalOperator);
  const tableVariant = isInternalContourRole ? "ng-secretary" : isOperator ? "operator" : "applicant";

  const tableColumns = useApplicationsTableColumns({ tableVariant, clientType, dictionaries, tableApiRef: pageStore.tableApi });

  const { sort, filterItems, handleFiltersReset } = useFiltersLine<ApplicationsTableItem>(pageStore?.tableApi ?? null);

  const handleRowClick = (event: RowClickedEvent<ApplicationsTableItem>) => {
    const application = event.node.data;

    if (!application?.id) {
      return;
    }

    // Черновик редактируется сразу в форме создания. Заявка «Требуется доработка»
    // сначала открывается карточкой, откуда доступна кнопка «Доработать заявку».
    if (clientType === "external" && application.status === "DRAFT") {
      navigate({ to: "/applications/create", search: { draftId: application.id } });
      return;
    }

    navigate({ to: "/applications/$id", params: { id: application.id } });
  };

  useApplicationsTableDatasource();

  useEffect(() => {
    pageStore?.clearSelectedApplications();
  }, [search, pageStore?.tableFilter, pageStore]);

  useEffect(() => {
    if (!isOperator) {
      pageStore?.clearSelectedApplications();
    }
  }, [isOperator, pageStore]);

  useEffect(() => {
    pageStore?.tableApi?.refreshInfiniteCache();
  }, [pageStore?.tableFilter, pageStore?.tableApi, pageStore?.refreshVersion]);

  useEffect(() => {
    if (!isDictionariesLoading) {
      pageStore?.tableApi?.refreshCells({ force: true });
    }
  }, [isDictionariesLoading, pageStore?.tableApi]);

  return (
    <div className={cn("flex flex-1 flex-col overflow-hidden", className)}>
      {clientType === "internal" && monitoringMetric && (
        <div className="mb-4 flex items-center gap-2">
          <p className="subtitle-sm text-type-common">{monitoringMetric.label}</p>
          <Button
            type="button"
            theme="text"
            size="sm"
            icon={<IconClose className="size-5" />}
            aria-label="Сбросить фильтр мониторинга"
            onClick={() => setMonitoringSearch({ monitoringMetric: null, railway: null })}
          />
        </div>
      )}
      {pageStore?.tableApi && (
        <TableFiltersLine className="mb-4" onClear={handleFiltersReset} filterItems={filterItems} sort={sort} api={pageStore?.tableApi} />
      )}
      <Table
        suppressHeaderMenuButton
        className="block flex-1 basis-0"
        key={`applications-table-${tableVariant}`}
        tableId={`applications-table-${tableVariant}`}
        module="ekptd"
        rowHeight={40}
        quickFilterText={search ?? undefined}
        onGridReady={(event: GridReadyEvent<ApplicationsTableItem>) => {
          pageStore?.setTableApi(event.api);
        }}
        rowModelType="infinite"
        tableColumns={tableColumns}
        defaultColDef={DEFAULT_COL_DEF}
        overlayComponentSelector={(params) => {
          const overlayType = params.overlayType;

          if (overlayType !== "noRows" && overlayType !== "noMatchingRows") {
            return undefined;
          }

          const hasActiveSearchOrFilters = Boolean(search?.trim()) || filterItems.length > 0;

          return {
            component: ApplicationsEmptyOverlay,
            params: {
              hasCreateAction: tableVariant === "applicant" && !hasActiveSearchOrFilters,
              overlayType,
            },
          };
        }}
        onRowClicked={handleRowClick}
        cacheBlockSize={TABLE_ROWS_PER_PAGE}
        {...(isOperator
          ? {
              selectionType: "multiple" as const,
              onSelectRow: (rows: ApplicationsTableItem[]) => pageStore?.setSelectedApplications(rows),
              getRowId: ({ data }: { data?: ApplicationsTableItem }) => data?.id ?? "",
              rowSelection: {
                mode: "multiRow" as const,
                checkboxes: true,
                enableClickSelection: false,
                headerCheckbox: true,
              },
            }
          : {})}
        cellSelection={false}
        loadingCellRenderer={<div className="skeleton inline-block h-5 w-[calc(100%-2rem)]" />}
        rowClass="cursor-pointer"
      />
    </div>
  );
});
